/**
 * Top-level react component.
 *
 * React router (https://reacttraining.com/react-router/web/guides/philosophy)
 * is used for conditional rendering based on the current path and for
 * transitioning between views.
 *
 * This component initializes the auth store (from useStores) and redirects to
 * either / or /login based on whether a user is logged in or not.
 */
import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import RegistrationWizard from './components/auth/RegistrationWizard';
import Login from './components/auth/Login';
import AppLayout from './components/AppLayout';
import Privacy from './components/Privacy';
import { observer } from 'mobx-react';
import { useStores } from './hooks/useStores';
import Spinner from './components/Spinner';
import { refreshAccessToken } from './lib/auth/refresh';

// Dev-only diagram gallery (phase 6a marker/node pixel-QA + PNG-export spike).
// Lazy + guarded so the gallery (and its html-to-image import) never enters a
// production bundle. The guard uses process.env.NODE_ENV, which the Vite config
// statically replaces with a string LITERAL at build time (define block, the
// same mechanism the rest of the app relies on). esbuild folds the literal
// comparison, so in `vite build` (mode=production) the ternary collapses to
// `null`, the `import()` becomes unreferenced, and Rollup never emits the
// DevGallery chunk. (import.meta.env.DEV is NOT used here: this repo's custom
// `esbuild` loader override pre-empts Vite's import.meta.env replacement, so it
// would leak the route into prod; NODE_ENV is the reliable, app-wide define.)
const IS_DEV = process.env.NODE_ENV !== 'production';
const DevGallery = IS_DEV ? lazy(() => import('./diagram/DevGallery')) : null;
// Dev-only React Flow EDG render-parity surface (phase 6b). Same IS_DEV lazy
// guard as DevGallery so neither the route nor @xyflow/react leaks into a
// production bundle.
const DevEdg = IS_DEV ? lazy(() => import('./diagram/DevEdg')) : null;
// Dev-only React Flow FSM render-parity surface (phase 6d). Same IS_DEV lazy
// guard so neither the route nor @xyflow/react leaks into a production bundle.
const DevFsm = IS_DEV ? lazy(() => import('./diagram/DevFsm')) : null;

// Element for the /test-bugsnag-error route. Throws on render (only when that
// route matches) so the Bugsnag error boundary can be exercised. A component is
// required here — an inline IIFE would throw on every App render, not just on
// the matching route.
function ThrowBugsnagTestError(): never {
  throw new Error('This is a test error.');
}

/**
 * The auth store was injected with mobx-react (`inject(({ store }) => ({ auth:
 * store.auth }))`); it now comes from useStores(). The withRouter shim is
 * dropped — `useLocation()` provides `location.search` directly.
 */
export default observer(function App() {
  const store = useStores();
  const auth = store.auth;
  const location = useLocation();

  // Initialize the auth store once on mount (was componentDidMount). The
  // `!auth.initialized` guard is preserved so a re-mount never re-initializes.
  //
  // Bootstrap the Bearer transport FIRST: exchange the httpOnly refresh cookie
  // (set by the last login, dual-accept) for a short-lived access token via
  // `POST /auth/token/refresh`, so the very first `/auth/me` and every later
  // `/api/*` call authenticates over Bearer rather than the session cookie. The
  // refresh is best-effort: a 401 (no/invalid refresh cookie → logged out) is
  // swallowed, leaving the in-memory token empty, and `auth.initialize()` then
  // sees `me()` return null and shows the unchanged logged-out UX.
  useEffect(() => {
    if (!auth.initialized) {
      refreshAccessToken()
        .catch(() => undefined)
        .then(() => auth.initialize());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!auth.initialized) {
    // Boot spinner mounts as the direct child of #root; the old
    // `#root > .preloader { padding-top: 60px }` rule is applied here at the
    // call site so it does not leak to the other (Suspense/modal) usages.
    return <Spinner className="pt-[60px]" />;
  }

  const loggedIn = auth.authenticated;
  return (
    <Routes>
      <Route path="/test-bugsnag-error" element={<ThrowBugsnagTestError />} />
      <Route path="/privacy" element={<Privacy />} />
      {DevGallery && (
        <Route
          path="/dev/gallery"
          element={
            <Suspense fallback={<Spinner />}>
              <DevGallery />
            </Suspense>
          }
        />
      )}
      {DevEdg && (
        <Route
          path="/dev/edg/:projectId"
          element={
            <Suspense fallback={<Spinner />}>
              <DevEdg />
            </Suspense>
          }
        />
      )}
      {DevFsm && (
        <Route
          path="/dev/fsm/:projectId/:objectTypeId?"
          element={
            <Suspense fallback={<Spinner />}>
              <DevFsm />
            </Suspense>
          }
        />
      )}
      <Route
        path="/register"
        element={
          loggedIn ? <Navigate to="/" replace /> : <RegistrationWizard query={location.search} />
        }
      />
      <Route path="/login" element={loggedIn ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/*" element={loggedIn ? <AppLayout /> : <Navigate to="/login" replace />} />
    </Routes>
  );
});
